import React, { useState, useEffect } from 'react';
import { Upload, Check, Image, Headphones, X } from 'lucide-react';
import { FormField } from '../../../types/formBuilder';
import { CachedImage } from '../../CachedImage';
import { VideoThumbnail } from '../../VideoThumbnail';
import { getAssetMediaType } from '../../../utils/formPreviewHelpers';
import { useAppStore } from '../../../store/useAppStore';
import { useUserAssets } from '../../../hooks/useUserAssets';
import { uploadFile } from '../../../utils/supabaseStorage';
import { LoadingSpinner } from '../../LoadingSpinner';

interface FileInputProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  /** Page / form app id for asset tagging (from Page Settings). */
  appId?: string;
}

export const FileInput: React.FC<FileInputProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  appId = '',
}) => {
  const { session } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Record<string, any>>({});
  const [isUploading, setIsUploading] = useState(false);

  const { assets, loading, error, refetch } = useUserAssets();

  // Initialize selected assets from current value
  useEffect(() => {
    if (value) {
      const initialSelection: Record<string, any> = {};
      if (field.multiple && Array.isArray(value)) {
        value.forEach(asset => {
          if (asset?.id) {
            initialSelection[asset.id] = asset;
          }
        });
      } else if (value?.id) {
        initialSelection[value.id] = value;
      }
      setSelectedAssets(initialSelection);
    } else {
      setSelectedAssets({});
    }
  }, [value, field.multiple]);

  // Get accepted file types from field configuration
  const getAcceptedTypes = (): ('image' | 'video' | 'audio')[] => {
    if (!field.accept) return ['image', 'video', 'audio']; // Show all if no restriction
    
    const accept = field.accept.toLowerCase();
    const types: ('image' | 'video' | 'audio')[] = [];
    
    if (accept.includes('image')) types.push('image');
    if (accept.includes('video')) types.push('video');
    if (accept.includes('audio')) types.push('audio');
    
    // If no specific type found, try to infer from extensions
    if (types.length === 0) {
      if (accept.includes('.jpg') || accept.includes('.png') || accept.includes('.gif') || accept.includes('.webp')) {
        types.push('image');
      }
      if (accept.includes('.mp4') || accept.includes('.avi') || accept.includes('.mov') || accept.includes('.webm')) {
        types.push('video');
      }
      if (accept.includes('.mp3') || accept.includes('.wav') || accept.includes('.flac') || accept.includes('.aac')) {
        types.push('audio');
      }
    }
    
    return types.length > 0 ? types : ['image']; // Default to image if nothing found
  };

  const acceptedTypes = getAcceptedTypes();

  // Filter assets based on field's accepted file types
  const filteredAssets = assets.filter(asset => {
    const assetType = getAssetMediaType(asset);
    return acceptedTypes.includes(assetType);
  });

  const handleAssetToggle = (asset: any) => {
    setSelectedAssets(prev => {
      const newMap = { ...prev };
      
      if (newMap[asset.id]) {
        delete newMap[asset.id];
      } else {
        if (field.multiple) {
          const maxFiles = field.fileLimit || 5;
          if (Object.keys(newMap).length < maxFiles) {
            newMap[asset.id] = asset;
          }
        } else {
          Object.keys(newMap).forEach(key => delete newMap[key]);
          newMap[asset.id] = asset;
        }
      }
      
      return newMap;
    });
  };

  const handleConfirmSelection = () => {
    const selectedAssetsList = Object.values(selectedAssets);
    
    if (selectedAssetsList.length > 0) {
      if (field.multiple) {
        onChange(selectedAssetsList);
      } else {
        onChange(selectedAssetsList[0]);
      }
    } else {
      onChange(field.multiple ? [] : null);
    }
    
    setIsExpanded(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    if (!session?.hy_user_id) return;
    const uploadAppId = appId?.trim() ?? '';
    if (!uploadAppId) {
      console.error('[FileInput] appId missing — set App ID in Page Settings for this page.');
      return;
    }

    setIsUploading(true);

    try {
      const uploadedAssets: any[] = [];
      let successCount = 0;

      for (const file of files) {
        const result = await uploadFile(
          file,
          session.hy_user_id,
          'uploaded',
          false,
          uploadAppId
        );

        if (result.success) {
          successCount++;
          const fileType = getFileTypeFromFile(file);
          uploadedAssets.push({
            id: result.assetId,
            public_url: result.publicUrl,
            filename: file.name,
            file_size: file.size,
            media_type: fileType,
            file_extension: file.name.split('.').pop()?.toLowerCase(),
            created_at: new Date().toISOString(),
            hy_user_id: session.hy_user_id,
            isaigenerated: false
          });
        }
      }

      if (successCount > 0) {
        await refetch();
        
        // Auto-select uploaded assets
        if (uploadedAssets.length > 0) {
          const newSelectedAssets: Record<string, any> = {};
          
          if (field.multiple) {
            const maxFiles = field.fileLimit || 5;
            const currentCount = Object.keys(selectedAssets).length;
            const remainingSlots = maxFiles - currentCount;
            const assetsToSelect = uploadedAssets.slice(0, remainingSlots);
            
            Object.assign(newSelectedAssets, selectedAssets);
            assetsToSelect.forEach(asset => {
              newSelectedAssets[asset.id] = asset;
            });
          } else {
            newSelectedAssets[uploadedAssets[0].id] = uploadedAssets[0];
          }
          
          setSelectedAssets(newSelectedAssets);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const getFileTypeFromFile = (file: File): 'image' | 'video' | 'audio' => {
    const fileType = file.type.toLowerCase();
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    if (fileType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) {
      return 'image';
    }
    if (fileType.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      return 'video';
    }
    if (fileType.startsWith('audio/') || ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
      return 'audio';
    }
    return 'image'; // default
  };

  // Use field's accept attribute or build from accepted types
  const getAcceptAttribute = (): string => {
    if (field.accept) return field.accept;
    
    // Build accept string from accepted types
    const acceptStrings: string[] = [];
    if (acceptedTypes.includes('image')) acceptStrings.push('image/*');
    if (acceptedTypes.includes('video')) acceptStrings.push('video/*');
    if (acceptedTypes.includes('audio')) acceptStrings.push('audio/*');
    
    return acceptStrings.join(',') || 'image/*';
  };

  const renderAssetPreview = (asset: any) => {
    const mediaType = getAssetMediaType(asset);
    
    switch (mediaType) {
      case 'image':
        return (
          <CachedImage
            src={asset.public_url}
            alt="Asset"
            className="w-full h-full object-cover"
            fallback={
              <div className="w-full h-full flex items-center justify-center text-th-secondary bg-elevated">
                <Image className="w-4 h-4" />
              </div>
            }
          />
        );
      
      case 'video':
        return (
          <div className="w-full h-full">
            <VideoThumbnail
              src={asset.public_url}
              className="w-full h-full"
              showPlayButton={true}
              onClick={() => {
                console.log('Video clicked:', asset.filename);
              }}
            />
          </div>
        );
      
      case 'audio':
        return (
          <div className="w-full h-full bg-status-warning/20 flex items-center justify-center">
            <Headphones className="w-4 h-4 text-status-warning" />
          </div>
        );
      
      default:
        return (
          <div className="w-full h-full bg-elevated flex items-center justify-center">
            <Image className="w-4 h-4 text-th-secondary" />
          </div>
        );
    }
  };

  const isSelected = (assetId: string): boolean => {
    return !!selectedAssets[assetId];
  };

  // Compact view when not expanded
  if (!isExpanded) {
    return (
      <div className="space-y-3">
        {/* Selected Assets Preview */}
        {value && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {(field.multiple && Array.isArray(value) ? value.slice(0, 3) : [value]).map((asset, index) => (
              <div key={asset.id || index} className="bg-elevated rounded-lg overflow-hidden border border-subtle">
                {renderAssetPreview(asset)}
              </div>
            ))}
            {field.multiple && Array.isArray(value) && value.length > 3 && (
              <div className="aspect-video bg-elevated/50 border border-subtle rounded-lg flex items-center justify-center">
                <span className="text-th-secondary text-sm">+{value.length - 3}</span>
              </div>
            )}
          </div>
        )}

        {/* Select Button */}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          disabled={disabled}
          className="w-full border-2 border-dashed border-subtle hover:border-brand/50 rounded-lg p-4 text-th-secondary hover:text-brand transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex flex-col items-center space-y-2">
            <Upload className="w-5 h-5" />
            <span className="font-medium text-sm">
              {value ? 'Change Selection' : field.multiple ? 'Select Assets' : 'Select Asset'}
            </span>
          </div>
        </button>
      </div>
    );
  }

  // Expanded inline gallery view
  return (
    <div className="space-y-4 border border-subtle rounded-xl bg-elevated p-4">
      {/* Header with Select and Upload Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {/* Small Upload Button */}
          <input
            type="file"
            multiple
            accept={getAcceptAttribute()}
            onChange={handleFileUpload}
            className="hidden"
            id="upload-files-input"
            disabled={isUploading}
          />
          <label
            htmlFor="upload-files-input"
            className={`flex items-center space-x-1 px-3 py-2 bg-status-warning text-base rounded-lg text-sm font-bold transition-all cursor-pointer border border-subtle shadow-md ${
              isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-[0.98] hover:bg-brand-hover'
            }`}
            title="Upload new files"
          >
            {isUploading ? (
              <LoadingSpinner size="sm" className="text-base" />
            ) : (
              <Upload className="w-3 h-3 text-th-tertiary" />
            )}
            <span className="text-th-tertiary">Upload</span>
          </label>
        </div>

        {/* Select Button - Moved to top, disabled until selection */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleConfirmSelection}
            disabled={Object.keys(selectedAssets).length === 0}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${
              Object.keys(selectedAssets).length === 0
                ? 'bg-elevated/50 text-th-secondary cursor-not-allowed border-subtle/30'
                : 'bg-status-success text-th-tertiary border-subtle hover:scale-105 active:scale-[0.98] shadow-md animate-pulse'
            }`}
            title={Object.keys(selectedAssets).length === 0 ? 'Select assets first' : 'Confirm selection'}
          >
            {Object.keys(selectedAssets).length === 0 ? (
              'Select'
            ) : (
              `Select (${Object.keys(selectedAssets).length})`
            )}
          </button>
          
          <button
            onClick={() => setIsExpanded(false)}
            className="p-2 text-th-tertiary hover:text-th-primary transition-colors rounded-lg hover:bg-surface border border-transparent hover:border-subtle"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>


      {/* Assets Grid */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center space-y-2">
              <LoadingSpinner size="md" />
              <p className="text-th-secondary text-sm">Loading assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-status-error text-4xl mb-2">⚠️</div>
            <p className="text-status-error text-sm">{error}</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📁</div>
            <p className="text-th-secondary text-sm">No {acceptedTypes.join('/')} files found</p>
            <p className="text-th-secondary text-xs">Upload {acceptedTypes.join('/')} files using the button above</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className="relative cursor-pointer group"
                onClick={() => handleAssetToggle(asset)}
              >
                <div className={`w-full rounded-lg overflow-hidden transition-all ${
                  isSelected(asset.id) 
                    ? 'ring-3 ring-status-success scale-95 shadow-lg shadow-status-success/30'
                    : 'hover:scale-105 active:scale-[0.98] group-hover:ring-2 group-hover:ring-brand/50'
                }`}>
                  {/* Asset Preview */}
                  <div className="w-full bg-elevated">
                    {renderAssetPreview(asset)}
                  </div>

                  {/* Selection Overlay */}
                  {isSelected(asset.id) && (
                    <div className="absolute inset-0 bg-base/40 flex items-center justify-center backdrop-blur-sm">
                      <div className="w-10 h-10 bg-elevated rounded-full flex items-center justify-center shadow-xl border-2 border-subtle ring-2 ring-base/30 animate-pulse">
                        <Check className="w-5 h-5 text-th-secondary" strokeWidth={2.5} />
                      </div>
                    </div>
                  )}

                  {/* Hover Overlay for unselected assets */}
                  {!isSelected(asset.id) && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 bg-elevated/90 backdrop-blur-sm rounded-full flex items-center justify-center border border-subtle">
                          <span className="text-th-secondary text-xs font-bold">+</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Media Type Badge */}
                  <div className="absolute bottom-1 right-1">
                    <span className="text-th-secondary text-xs px-1.5 py-0.5 bg-base/85 backdrop-blur-sm rounded-full text-[10px] uppercase font-medium border border-subtle">
                      {getAssetMediaType(asset)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};